import {
  Flex,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useRevalidator } from "react-router-dom";
import { UnexpectedErrorContent } from "components/errors/components/unexpected-error-content";
import { Form } from "components/form";
import { FormField } from "components/form-field";
import { CountedTextInput } from "components/inputs/counted-text-input";
import { LoadingScreen } from "components/loading-screen";
import { ScreenWrapper } from "components/screen-wrapper";
import { AgencySettingsPageCards } from "modules/agency-settings/components/agency-settings-page/agency-settings-page-cards";
import { AgencySettingsPageTitle } from "modules/agency-settings/components/agency-settings-page/agency-settings-page-title";
import {
  vendorLoginEmailSettingsModel,
  type VendorLoginEmailImageChoice,
  type VendorLoginEmailSettings,
} from "modules/agency-settings/models/vendor-login-email-settings-model";
import { getTraceId } from "utils/get-trace-id";

const BODY_TEXT_MAX = 2000;

// The "Custom Email" template type is derived from / persisted as the body text:
// an empty body means the vendor email renders with Rex's default content, so we
// don't persist a separate type column (see CRM-13717). The radio is purely a UI
// affordance that gates the body + image fields.
type VendorEmailTemplateType = "rex_default" | "custom";

type VendorEmailFormValues = {
  template_type: VendorEmailTemplateType;
  body_text: string;
  image_choice: VendorLoginEmailImageChoice;
};

// Radio input for the template type — Rex default vs a customised email. Wraps
// Chakra's RadioGroup so it plugs into FormField's {value, onChange} contract.
function TemplateTypeRadioInput({
  value,
  onChange,
}: {
  value?: VendorEmailTemplateType;
  onChange: (value: VendorEmailTemplateType) => void;
}) {
  return (
    <RadioGroup
      value={value}
      onChange={(next) => onChange(next as VendorEmailTemplateType)}
    >
      <Stack spacing={3}>
        <Radio value={"rex_default"}>Rex default</Radio>
        <Radio value={"custom"}>Custom email</Radio>
      </Stack>
    </RadioGroup>
  );
}

// Radio input for the hero image choice — wraps Chakra's RadioGroup so it plugs
// into FormField's {value, onChange} controller contract.
function ImageChoiceRadioInput({
  value,
  onChange,
}: {
  value?: VendorLoginEmailImageChoice;
  onChange: (value: VendorLoginEmailImageChoice) => void;
}) {
  return (
    <RadioGroup
      value={value}
      onChange={(next) => onChange(next as VendorLoginEmailImageChoice)}
    >
      <Stack spacing={3}>
        <Radio value={"rex_default"}>Use the Rex default image</Radio>
        <Radio value={"listing_image_1"}>Use the primary listing image</Radio>
      </Stack>
    </RadioGroup>
  );
}

function VendorEmailScreenWithData({
  settings,
}: {
  settings: VendorLoginEmailSettings;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const revalidator = useRevalidator();

  const hasCustomBody =
    !!settings.body_text && settings.body_text.trim() !== "";

  const form = useForm<VendorEmailFormValues>({
    defaultValues: {
      template_type: hasCustomBody ? "custom" : "rex_default",
      body_text: settings.body_text ?? "",
      image_choice: settings.image_choice,
    },
  });

  const templateType = form.watch("template_type");
  const isCustom = templateType === "custom";

  const updateMutation = useMutation({
    ...vendorLoginEmailSettingsModel.mutations.update(),
    onSuccess: () => {
      toast({
        title: "Vendor email updated",
        description: "Your vendor login email settings have been saved.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      queryClient.invalidateQueries({
        queryKey: vendorLoginEmailSettingsModel.queries.get().queryKey,
      });
      revalidator.revalidate();
    },
    onError: () => {
      toast({
        title: "Update failed",
        description: (
          <>
            <Text>
              There was an error saving your vendor email settings. Please try
              again.
            </Text>
            <small>Trace ID: {getTraceId()}</small>
          </>
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const handleSubmit = async (values: VendorEmailFormValues) => {
    // Rex default == no custom content: persist an empty body and the default
    // image so wings-api renders the standard email. The custom body + image are
    // only sent when the user explicitly opted into a custom email.
    const payload =
      values.template_type === "custom"
        ? { body_text: values.body_text, image_choice: values.image_choice }
        : { body_text: "", image_choice: "rex_default" as const };

    await updateMutation.mutateAsync(payload);
    form.reset(values);
  };

  return (
    <ScreenWrapper>
      <Form
        id={"vendor-email"}
        onSubmit={handleSubmit}
        form={form}
        enableSaveCancelOverlay
        ctaText={"Save"}
      >
        <Flex direction={"column"} gap={8}>
          <AgencySettingsPageTitle
            title={"Rex Hub Login Email"}
            description={
              "Customise the login link email your vendors receive for Rex Hub"
            }
          />
          <AgencySettingsPageCards
            heading={"Email template"}
            description={
              'For custom emails, add your own message and choose the image. Your message appears below an automatic greeting, so there\'s no need to add your own "Hello".'
            }
          >
            <Flex direction={"column"} gap={12}>
              <FormField
                name={"template_type"}
                Input={TemplateTypeRadioInput}
              />
              {isCustom ? (
                <>
                  <FormField
                    name={"body_text"}
                    label={"Email body"}
                    required
                    Input={CountedTextInput}
                    inputProps={{
                      Input: Textarea,
                      max: BODY_TEXT_MAX,
                      placeholder: "Add a message to your vendors.",
                    }}
                  />
                  <FormField
                    name={"image_choice"}
                    label={"Email image"}
                    required
                    Input={ImageChoiceRadioInput}
                  />
                </>
              ) : null}
            </Flex>
          </AgencySettingsPageCards>
        </Flex>
      </Form>
    </ScreenWrapper>
  );
}

export function VendorEmailScreen() {
  const {
    data: settings,
    isLoading,
    error,
  } = useQuery(vendorLoginEmailSettingsModel.queries.get());

  if (error) {
    return <UnexpectedErrorContent error={error} />;
  }

  if (!settings || isLoading) {
    return <LoadingScreen loadingText={"Loading vendor email settings..."} />;
  }

  return <VendorEmailScreenWithData settings={settings} />;
}
